import {useEffect, useState} from "react";
import {Autocomplete, Box, Chip, CircularProgress, FormControl, Grid, MenuItem, Select, TextField} from "@mui/material";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {SnackBar} from "components/common";
import {Auth} from "components/Authorization";
import commonStyles from "../commonStyles";

const AUTO_ACTION_MODE = {
    ALL: 'ALL',
    NONE: 'NONE',
    SELECTED: 'SELECTED',
};

function handleApiError(error, fallback) {
    if (_.get(error, 'response.status') === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, 'response.data.message') || _.get(error, 'message') || fallback;
    SnackBar.showMessage(message);
}

function toStringList(value) {
    if (Array.isArray(value)) {
        return value.filter((item) => typeof item === 'string' && item.length > 0);
    }
    if (typeof value === 'string' && value.length > 0) {
        return value.split(',').map((item) => item.trim()).filter(Boolean);
    }
    return [];
}

function StringListField({label, values, onChange, placeholder, disabled}) {
    return (
        <>
            <Box sx={commonStyles.labelMedium}>{label}</Box>
            <Autocomplete
                multiple
                freeSolo
                disabled={disabled}
                options={[]}
                value={values}
                onChange={(_event, next) => onChange(next)}
                renderTags={(tagValues, getTagProps) =>
                    tagValues.map((option, index) => (
                        <Chip
                            variant="outlined"
                            size="small"
                            label={option}
                            disabled={disabled}
                            {...getTagProps({index})}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={placeholder}
                        sx={styles.input}
                    />
                )}
                sx={styles.autocomplete}
            />
        </>
    );
}

export default function InternalNotesSettings({msgDataSource, label}) {
    const msgDataSourceId = msgDataSource?.id;

    const [mode, setMode] = useState(msgDataSource?.internalNoteAutoActionMode || AUTO_ACTION_MODE.NONE);
    const [emails, setEmails] = useState(() => toStringList(msgDataSource?.internalNoteAutoActionEmails));
    const [suffixes, setSuffixes] = useState(() => toStringList(msgDataSource?.internalNoteAutoActionEmailSuffixes));
    const [submittingField, setSubmittingField] = useState(null);

    useEffect(() => {
        setMode(msgDataSource?.internalNoteAutoActionMode || AUTO_ACTION_MODE.NONE);
        setEmails(toStringList(msgDataSource?.internalNoteAutoActionEmails));
        setSuffixes(toStringList(msgDataSource?.internalNoteAutoActionEmailSuffixes));
    }, [
        msgDataSource?.id,
        msgDataSource?.internalNoteAutoActionMode,
        msgDataSource?.internalNoteAutoActionEmails,
        msgDataSource?.internalNoteAutoActionEmailSuffixes,
    ]);

    const listsEnabled = mode === AUTO_ACTION_MODE.SELECTED;
    const busy = submittingField != null;

    const persist = async (field, payload, applyResponse) => {
        if (!msgDataSourceId || submittingField) return;
        setSubmittingField(field);
        try {
            const response = await organizationSettingsService.updateMsgDataSourceInternalNoteAutoAction(
                msgDataSourceId,
                payload
            );
            applyResponse(response?.data || {});
            SnackBar.showMessage('Internal note auto-action settings updated.');
        } catch (error) {
            handleApiError(error, 'Failed to update internal note auto-action settings.');
            throw error;
        } finally {
            setSubmittingField(null);
        }
    };

    const handleModeChange = async (event) => {
        const previous = mode;
        const next = event.target.value;
        setMode(next);
        try {
            await persist('mode', {internalNoteAutoActionMode: next}, (data) => {
                if (data.internalNoteAutoActionMode) {
                    setMode(data.internalNoteAutoActionMode);
                }
            });
        } catch (_error) {
            setMode(previous);
        }
    };

    const handleEmailsChange = async (next) => {
        const previous = emails;
        setEmails(next);
        try {
            await persist('emails', {internalNoteAutoActionEmails: next}, (data) => {
                setEmails(toStringList(data.internalNoteAutoActionEmails));
            });
        } catch (_error) {
            setEmails(previous);
        }
    };

    const handleSuffixesChange = async (next) => {
        const previous = suffixes;
        setSuffixes(next);
        try {
            await persist('suffixes', {internalNoteAutoActionEmailSuffixes: next}, (data) => {
                setSuffixes(toStringList(data.internalNoteAutoActionEmailSuffixes));
            });
        } catch (_error) {
            setSuffixes(previous);
        }
    };

    if (!msgDataSourceId) {
        return null;
    }

    return (
        <Box sx={styles.container}>
            <Box component={'h4'} sx={commonStyles.labelBold}>
                {label?.INTERNAL_NOTES_TEAMS}
            </Box>
            <Grid container columnSpacing={4} rowSpacing={1}>
                <Grid item xs={12}>
                    <Box sx={commonStyles.labelMedium}>
                        {label?.AUTO_ACTION_INTERNAL_NOTES}
                    </Box>
                    <Box sx={styles.selectRow}>
                        <FormControl size="small" sx={styles.select}>
                            <Select
                                value={mode}
                                disabled={busy}
                                onChange={handleModeChange}
                            >
                                <MenuItem value={AUTO_ACTION_MODE.ALL}>
                                    {label?.AUTO_ACTION_INTERNAL_NOTES_ALL}
                                </MenuItem>
                                <MenuItem value={AUTO_ACTION_MODE.NONE}>
                                    {label?.AUTO_ACTION_INTERNAL_NOTES_NONE}
                                </MenuItem>
                                <MenuItem value={AUTO_ACTION_MODE.SELECTED}>
                                    {label?.AUTO_ACTION_INTERNAL_NOTES_SELECTED}
                                </MenuItem>
                            </Select>
                        </FormControl>
                        {submittingField === 'mode' && (
                            <CircularProgress size={16} sx={styles.spinner} />
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                    <StringListField
                        label={label?.AUTO_ACTION_INTERNAL_NOTES_EMAILS}
                        values={emails}
                        onChange={handleEmailsChange}
                        placeholder={label?.ADD_EMAIL_PLACEHOLDER}
                        disabled={!listsEnabled || busy}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <StringListField
                        label={label?.AUTO_ACTION_INTERNAL_NOTES_SUFFIXES}
                        values={suffixes}
                        onChange={handleSuffixesChange}
                        placeholder={label?.ADD_SUFFIX_PLACEHOLDER}
                        disabled={!listsEnabled || busy}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

const styles = {
    container: {
        mb: 2,
    },
    selectRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
    },
    select: {
        minWidth: '160px',
        '& .MuiOutlinedInput-root': {
            fontFamily: 'LarsseitRegular',
            fontSize: '14px',
        },
    },
    autocomplete: {
        mt: 0.5,
        maxWidth: '420px',
    },
    input: {
        '& .MuiOutlinedInput-root': {
            fontFamily: 'LarsseitRegular',
            fontSize: '14px',
        },
    },
    spinner: {
        color: '#003300',
    },
};
